import AppPage from "layouts/AppPage";
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router";
import { useNavbar } from "hooks/useNavbar";
import { TabSelector, TabOption } from "components/TabSelector";
import { useNewHireListByManagerIdQuery } from "features/NewHireJourney/hooks";
import ControlledDataTable from "components/Table/ControlledDataTable";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Box, HStack } from "@chakra-ui/react";
import CDS from "design-system";
import EmptyState from "components/EmptyComponents/EmptyState";
import { Image } from "@chakra-ui/react";
import CoworkersSVG from "assets/undraw_co_workers.svg";
import CelebrationSVG from "assets/undraw_celebration.svg";
import BeachDay from "assets/undraw_beach_day.svg";
import { InternalLink } from "components/Link";
import { NewHireJourney } from "models/v2/newHireJourney";
import { PrimaryButton } from "components/Button";
import ListProgress from "features/NewHireJourney/Components/Progress/ListProgress";
import { useCurrentUserQuery } from "features/User/hooks";
import NewHireJourneyStatus from "components/Tag/NewHireJourneyStatusTag";
import NewHireAvatarNameStart from "features/NewHireJourney/Components/NewHireHeader/NewHireAvatarNameStart";
import { useAnalytics } from "services/analytics";

type NewHireFilter = "all" | "in-progress" | "upcoming" | "past";

const columnHelper = createColumnHelper<NewHireJourney>();

const makeColumns: (
  trackEvent: (eventName: string, eventData?: Record<string, any>) => void,
) => ColumnDef<NewHireJourney, any>[] = (trackEvent) => [
  columnHelper.accessor((row) => row, {
    id: "newHire",
    enableSorting: false,
    header: "New Hire",
    cell: (row) => (
      <InternalLink
        to={`/manager/new-hires/${row.getValue()?.id}`}
        onClick={() =>
          trackEvent("new_hire.click", {
            source: "manager_dashboard",
            source_type: "name",
          })
        }
      >
        <NewHireAvatarNameStart newHireJourney={row.getValue()} />
      </InternalLink>
    ),
    sortingFn: (rowA, rowB) => {
      const a = rowA.getValue("startDate");
      const b = rowB.getValue("startDate");

      const dateA = a ? new Date(a) : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
      const dateB = b ? new Date(b) : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);

      return dateB.getTime() - dateA.getTime();
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "summary",
    header: "Tasks",
    cell: (row) => <ListProgress newHireJourney={row.getValue()} />,
    enableSorting: false,
    meta: {
      headerHStackProps: {
        pl: "36px",
      },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "status",
    header: "Status",
    cell: (row) => <NewHireJourneyStatus newHireJourney={row.getValue()} />,
    sortingFn: (rowA, rowB) => {
      const statusOrder = { pending: 0, upcoming: 1, active: 2, archived: 3 };
      const statusA = rowA.getValue("status")?.status || "";
      const statusB = rowB.getValue("status")?.status || "";

      return (statusOrder[statusA] || 999) - (statusOrder[statusB] || 999);
    },
  }),
  columnHelper.display({
    id: "actions",
    header: "",
    enableSorting: false,
    cell: ({ row }) => (
      <HStack spacing={2}>
        <InternalLink
          to={`/manager/new-hires/${row.original.id}`}
          onClick={() =>
            trackEvent("new_hire.click", {
              source: "manager_dashboard",
              source_type: "view_detail",
            })
          }
        >
          <PrimaryButton size="xs">View Detail</PrimaryButton>
        </InternalLink>
      </HStack>
    ),
  }),
];

const FILTER_EMPTY_STATES: Record<NewHireFilter, { svg: string; message: string }> = {
  all: {
    svg: CoworkersSVG,
    message: "No new hires assigned to you yet!\n\nYour People Team will add them here when someone new joins your team. Check back soon! 🙌",
  },
  "in-progress": {
    svg: CelebrationSVG,
    message: "No new hires currently in progress.\n\nOnce a new hire's journey kicks off, they'll show up here.",
  },
  upcoming: {
    svg: BeachDay,
    message: "Nothing upcoming just yet!\n\nEnjoy the calm — new hires will appear here when they're scheduled to start soon.",
  },
  past: {
    svg: CelebrationSVG,
    message: "No past new hires to show.\n\nCompleted onboarding journeys will appear here over time.",
  },
};

function ManagerEmptyState({ filter }: { filter: NewHireFilter }) {
  const { svg, message } = FILTER_EMPTY_STATES[filter];
  return (
    <EmptyState message={message}>
      <Image maxWidth="280px" maxHeight="280px" src={svg} />
    </EmptyState>
  );
}

function filterByStatus(newHireJourneys: NewHireJourney[] | undefined, status: NewHireFilter) {
  if (!newHireJourneys) {
    return [];
  }

  switch (status) {
    case "in-progress":
      // Active: not pending AND start date is less than 1 year old
      return newHireJourneys.filter((newHireJourney) => newHireJourney.status === "active");
    case "upcoming":
      return newHireJourneys.filter((newHireJourney) => newHireJourney.status === "upcoming");
    case "past":
      // Past: not pending AND start date is more than 1 year old
      return newHireJourneys.filter((newHireJourney) => newHireJourney.status === "archived");
    default:
      return newHireJourneys;
  }
}

export default function ManagerDashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setPageTitle } = useNavbar();
  const { data: currentUser } = useCurrentUserQuery();

  const { trackPageView, trackEvent } = useAnalytics();

  // Get filter from query params, default to "all"
  const selectedNewHires = (searchParams.get("view") || "all") as NewHireFilter;

  const { data: allNewHireJourneys = [], isLoading } = useNewHireListByManagerIdQuery(currentUser?.id || "", {
    enabled: !!currentUser?.id,
  });

  const newHireJourneys = useMemo(
    () => filterByStatus(allNewHireJourneys, selectedNewHires),
    [allNewHireJourneys, selectedNewHires],
  );

  useEffect(() => {
    setPageTitle("Manager Dashboard");
    return () => {
      setPageTitle(null);
    };
  }, [setPageTitle]);

  useEffect(() => {
    trackPageView("dashboard", { dashboard_type: "manager" });
  }, [trackPageView]);

  // Set default filter if none provided
  useEffect(() => {
    if (!searchParams.get("view")) {
      setSearchParams({ view: "all" }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleFilterChange = (value: string) => {
    trackEvent("tab.select", {
      source: "manager_dashboard",
      source_type: "time_period_filter",
      selected_value: value,
    });
    setSearchParams({ view: value });
  };

  const columns = useMemo(() => makeColumns(trackEvent), [trackEvent]);

  return (
    <AppPage>
      <CDS.Text18 fontSize="xl" fontWeight="semibold" color="content.primary" mb={4}>
        My New Hires
      </CDS.Text18>
      <TabSelector size="sm" value={selectedNewHires} onChange={handleFilterChange}>
        <TabOption value="all">All</TabOption>
        <TabOption value="in-progress">In Progress</TabOption>
        <TabOption value="upcoming">Upcoming</TabOption>
        <TabOption value="past">Past</TabOption>
      </TabSelector>

      <Box mt={4} />

      <ControlledDataTable
        data={newHireJourneys}
        columns={columns}
        isLoading={isLoading}
        sx={{ backgroundColor: "transparent" }}
        defaultSort={[{ id: "status", desc: false }]}
        emptyStateComponent={<ManagerEmptyState filter={selectedNewHires} />}
      />
    </AppPage>
  );
}
